import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { useSelector } from "react-redux";
import { useAppThunkDispatch, RootState } from "redux/store";
import { FeatherIcon } from "component/shared/feather-icon";
import { withMediaQuery, WithMediaQuery } from "component/shared/with-media-query";
import { updateCampaignCampaignMessages } from "redux/slice/campaign-campaign-messages"
import { styles } from "./styles";
import { DragDropContext, Droppable, DropResult, DragStart } from "react-beautiful-dnd";
import { Card } from "./card";
import { CampaignCampaignMessage } from "model/campaign-campaign-message";
import { urls } from "routes/urls";
import { getCampaignCampaignMessagesByCampaignId } from "redux/selector";
import { getCampaignById } from "redux/selector";
import { TimeUnitType } from "type/time-unit";
import * as Router from "react-router-dom";
import { fetchCampaignCampaignMessages } from "redux/slice/campaign-campaign-messages"
import { getUserUnsafe } from "redux/selector";
import { generateId } from "shared/generate-id";
import * as env from "shared/env";

interface Props extends
	OwnProps,
	WithMediaQuery,
	Router.RouteComponentProps,
	WithStyles<typeof styles>
{
}

interface OwnProps {
	campaignId: number;
}

const Component: React.FC<Props> = ({ campaignId, mediaQuery, classes = {}, history }) => {
	// Redux hooks
	const dispatch = useAppThunkDispatch();
	const user = useSelector(getUserUnsafe);
	const campaignCampaignMessages = useSelector((state: RootState) => 
		getCampaignCampaignMessagesByCampaignId(state, campaignId)
	);
	const campaign = useSelector((state: RootState) => getCampaignById(state, campaignId));
	const loading = useSelector((state: RootState) => state.campaignCampaignMessages.loading);

	// Local state
	const [dndOrder, setDndOrder] = useState<number[]>([]);
	const [dragging, setDragging] = useState<string | null>(null);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [addMenuOpen, setAddMenuOpen] = useState<boolean>(false);

	// Ref
	const newMessageRef = useRef<HTMLInputElement>(null);

	// Memoized function to set DnD order
	const createDndOrder = useCallback(() => {
		const order: number[] = [];
		campaignCampaignMessages.forEach((campaignCampaignMessage) => {
			order.push(campaignCampaignMessage.id);
		});
		return order;
	}, [campaignCampaignMessages]);

	// Update dndOrder when campaignCampaignMessages changes
	useEffect(() => {
			const order = createDndOrder();
			setDndOrder(order);
	}, [campaignCampaignMessages, createDndOrder]);

	// Fetch campaign messages once on mount or when the campaignId changes
	useEffect(() => {
			dispatch(fetchCampaignCampaignMessages());
	}, [dispatch, campaignId]);

	// Copy function
	const copy = useCallback(() => {
		if (newMessageRef.current) {
			newMessageRef.current.scrollIntoView();
		}
	}, []);

	// Save function
	const save = useCallback((messages: CampaignCampaignMessage[]) => {
  // derive order from the reordered messages array
  const order = messages.map(message => message.id);
  setDndOrder(order);

  if (order.length > 0) {
	let index = 1;
	messages.forEach(campaignMessage => {
	  campaignMessage.messageOrder = index++;
	});
	dispatch(updateCampaignCampaignMessages({ campaignCampaignMessages: messages }));
  }
}, [dispatch]);

	// Drag handlers
	const onDragStart = useCallback((start: DragStart) => {
		setDragging(start.draggableId);
	}, []);

	const onDragEnd = useCallback((result: DropResult) => {
		const { draggableId, source, destination } = result;
		const dropped = campaignCampaignMessages.find((campaignCampaignMessage) => 
			campaignCampaignMessage.id === parseInt(draggableId));
		
		if (!dropped || !destination) {
			setDragging(null);
			return;
		}
		
		if ((destination.droppableId === source.droppableId) && (destination.index === source.index)) {
			setDragging(null);
			return;
		}
		
		const newMessages = [...campaignCampaignMessages];
		newMessages.splice(source.index, 1);
		newMessages.splice(destination.index, 0, {...dropped});
		save(newMessages);
		setDragging(null);
	}, [campaignCampaignMessages, save]);

	// Memoized function to get messages with day numbers
	const getWithDayNumber = useMemo(() => {
		const dayInMinutes: number = 1440;
		let dayNumber: number = 0;
		let fromTotalDays = 1;
		
		return campaignCampaignMessages.map(campaignCampaignMessage => {
			if (campaignCampaignMessage.campaignMessage.timeToSendNumber && campaignCampaignMessage.campaignMessage.timeToSendUnit) {
				if ((campaignCampaignMessage.campaignMessage.timeToSendUnit === TimeUnitType.MINUTES)) {
					fromTotalDays = fromTotalDays + campaignCampaignMessage.campaignMessage.timeToSendNumber / dayInMinutes;
					dayNumber = Math.floor(fromTotalDays);
				}
				if (campaignCampaignMessage.campaignMessage.timeToSendUnit === TimeUnitType.DAYS) {
					fromTotalDays = fromTotalDays + campaignCampaignMessage.campaignMessage.timeToSendNumber * dayInMinutes / dayInMinutes;
					dayNumber = Math.floor(fromTotalDays);
				}
			}
			campaignCampaignMessage.dayNumber = dayNumber;
			return campaignCampaignMessage;
		});
	}, [campaignCampaignMessages]);

	// Handle actions click
	const handleActionsClick = useCallback((event: HTMLElement) => {
		if (user && user.permissions.dripMms && user.textMessageOptIn) {
			setAnchorEl(event);
		} else {
			const url = urls.campaignCampaignEmailMessageNew(campaign);
			if (url) {
				history.push(url);
			}
		}
	}, [user, campaign, history]);

	// Event handlers
	const handleMenuClose = useCallback(() => {
		setAnchorEl(null);
	}, []);

	const handleEmailMenuClick = useCallback(() => {
		const url = urls.campaignCampaignEmailMessageNew(campaign);
		if (url) {
			history.push(url);
		}
	}, [campaign, history]);

	const handleTextMenuClick = useCallback(() => {
		const url = urls.campaignCampaignTextMessageNew(campaign);
		if (url) {
			history.push(url);
		}
	}, [campaign, history]);

	// Render add message component
	const renderAddMessage = useCallback(() => {
		const addMessageMenuId = generateId();
		
		return (
			<>
				<Mui.Button 
					color={"primary"}
					variant="contained"
					aria-controls={addMessageMenuId}
					aria-haspopup="true"
					onClick={(event) => handleActionsClick(event.currentTarget)}
					startIcon={
						<FeatherIcon>
							<Icons.PlusCircle />
						</FeatherIcon>
					}
				>
					Add Message 
				</Mui.Button>
				{user && user.permissions.dripMms && user.textMessageOptIn &&
					<Mui.Menu
						id={addMessageMenuId}
						anchorEl={anchorEl}
						keepMounted
						open={!!anchorEl}
						onClose={handleMenuClose}
						anchorOrigin={{
							vertical: "top",
							horizontal: "center",
						}}
						transformOrigin={{
							vertical: "top",
							horizontal: "center",
						}}
					>
						<Mui.MenuItem onClick={handleEmailMenuClick}>
							<Mui.ListItemIcon>
								<FeatherIcon>
									<Icons.Mail />
								</FeatherIcon>
							</Mui.ListItemIcon>
							<Mui.ListItemText primary="Email" />
						</Mui.MenuItem>
						<Mui.MenuItem onClick={handleTextMenuClick}>
							<Mui.ListItemIcon>
								<FeatherIcon>
									<Icons.MessageSquare />
								</FeatherIcon>
							</Mui.ListItemIcon>
							<Mui.ListItemText primary="Text Message" />
						</Mui.MenuItem>
					</Mui.Menu>
				}
			</>
		);
	}, [user, anchorEl, handleActionsClick, handleMenuClose, handleEmailMenuClick, handleTextMenuClick]);

	return (
		<Mui.Grid container direction="column" wrap="nowrap" justifyContent="center">
			{campaignCampaignMessages.length > 0 ? ( 
				<Mui.Grid item>
					<Mui.Grid container direction="column" wrap="nowrap" justifyContent="center">
						<Mui.Grid item className={classes.timeline}>
							<Mui.Typography className={classes.timelineLabel}>
								Subscriber Added
							</Mui.Typography>
							<Mui.Typography className={classes.TimelineIcon}>
								<FeatherIcon fontSize="inherit" >
									<Icons.ArrowDown/>
								</FeatherIcon>	
							</Mui.Typography>
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.Typography color="textSecondary">*Data provided only within the last 30 days is shown</Mui.Typography>
						</Mui.Grid>
						<Mui.Grid item >
							<DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
								<Droppable droppableId="column-1">
									{provided => (
										<Mui.Grid container direction="column" wrap="nowrap" ref={provided.innerRef} {...provided.droppableProps}>
											{dndOrder.length > 0 && dndOrder.map((id, index) => {
												const campaignCampaignMessage = getWithDayNumber.find((campaignCampaignMessage) => campaignCampaignMessage.id === id);												
												return (
													<Mui.Grid item key={index}>
														{campaignCampaignMessage && (
															<Card
																campaignCampaignMessage={campaignCampaignMessage}
																key={campaignCampaignMessage.id}
																index={index}
																dragging={dragging}
																onCopy={copy}
															/>
														)}
													</Mui.Grid>
												)
											})}
											{provided.placeholder}
										</Mui.Grid>
									)}
								</Droppable>
							</DragDropContext>
						</Mui.Grid>
						<Mui.Grid item className={classes.timeline}>
							<Mui.Typography className={classes.timelineLabel}>
								Campaign Complete
							</Mui.Typography>
						</Mui.Grid>
						<Mui.Grid item ref={newMessageRef} className={classes.add} style={{textAlign: mediaQuery ? "center" : undefined}}>
							{renderAddMessage()}
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
				) : (
					<>
						<Mui.Grid item>
							<Mui.Typography variant="h2" className={classes.addNew}>
								Add a new messages
							</Mui.Typography>
						</Mui.Grid>
						<Mui.Grid item ref={newMessageRef} className={classes.addNewButton}>
							{renderAddMessage()}
						</Mui.Grid>
					</>
				)}
		</Mui.Grid>			
	);
};

export const Messages = Router.withRouter(withMediaQuery("(max-width:600px)")(withStyles(Component, styles)));